import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import Animated from 'react-native-reanimated';
import { AppView, withAnimatedNavigationHeader, SCREEN_BOTTOM_PADDING, SCROLL_INDICATOR_INSETS } from 'design-system';
import Spacer from 'design-system/Spacer';
import Modal from 'utils/packages/Modal';
import { closedConnection, getBankConnections } from 'actions';
import orderBankLoginsByPriority from 'utils/api/orderBankLoginsByPriority';
import { isBankLoginConnection } from 'utils/creditReportAccounts';
import { useOpenConnectionLogin } from 'hooks/useOpenConnectionLogin';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectConnections, selectSavingsPlanConnectionId } from 'reducers/selectors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import ListEmptyView from 'design-system/ListEmptyView';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import LogoBankAccount from 'design-system/LogoBankAccount';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import BrokenConnectionCard from './components/BrokenConnectionCard';
import ConnectionUsedInAutosaveModal from './components/ConnectionUsedInAutosaveModal';
const keyExtractor = (item, index) => (item.id ?? index).toString();
const BrokenConnectionsScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const isWaiting = route.params?.isWaiting;
    const dispatch = useAppDispatch();
    const savingsPlanConnectionId = useAppSelector(selectSavingsPlanConnectionId);
    const connections = useAppSelector(selectConnections);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: isWaiting ? undefined : () => <HelpScreenButton articlesKey={HELP_ARTICLES_KEY.BANK_CONNECT}/>,
        });
    }, [isWaiting]);
    useEffect(() => {
        dispatch(getBankConnections());
    }, []);
    const allConnections = useMemo(() => orderBankLoginsByPriority(connections.filter((item) => item.id), true).filter(isBankLoginConnection), [connections]);
    const [renewingConnection, setRenewingConnection] = useState();
    useEffect(() => {
        if (!renewingConnection)
            return () => { };
        if (allConnections.find((connection) => connection.id === renewingConnection.id))
            return () => { };
        if (isWaiting) {
            navigation.goBack();
        }
        else {
            navigation.navigate('Success', {
                title: !allConnections.length ? 'All done!' : 'Success',
                icon: <LogoBankAccount data={renewingConnection.bankInfo} size="R56"/>,
                subTitle: `Your connection with ${renewingConnection.bankInfo.name} has been renewed!`,
                onContinue: () => {
                    if (!allConnections.length) {
                        navigation.pop();
                    }
                    navigation.goBack();
                    return true;
                },
                buttonText: 'Done',
            });
        }
        const timeout = setTimeout(() => setRenewingConnection(undefined), 300);
        return () => clearTimeout(timeout);
    }, [allConnections, isWaiting, renewingConnection]);
    const [isClosingId, setIsClosingId] = useState();
    const onCloseConnection = useCallback((connection) => {
        if (savingsPlanConnectionId === connection.id) {
            ConnectionUsedInAutosaveModal.show(() => navigation.navigate('AutoSave'), 'close');
            return;
        }
        if (renewingConnection?.id === connection.id) {
            setRenewingConnection(undefined);
        }
        Modal.showConfirmation({
            title: 'Are you sure?',
            subtitle: 'This connection will stop syncing. It will be moved to the Closed section but data will still be visible across the app.',
            confirmButtonTitle: 'Yes, let me close',
            cancelButtonTitle: 'No, thanks',
            onConfirm: async () => {
                setIsClosingId(connection.id);
                await dispatch(closedConnection(connection));
                setIsClosingId(undefined);
                dispatch(getBankConnections());
            },
        });
    }, [renewingConnection?.id, savingsPlanConnectionId]);
    const login = useOpenConnectionLogin(navigation);
    const openBankLogin = useCallback((connection) => {
        setRenewingConnection(connection);
        login({ type: 'connection', data: connection }, false);
    }, [login]);
    const renderItem = useCallback(({ item }) => (<BrokenConnectionCard data={item} onClose={onCloseConnection} onRenew={openBankLogin} hideClose={isWaiting} isClosing={isClosingId === item.id}/>), [onCloseConnection, openBankLogin, isWaiting, isClosingId]);
    const ListEmptyComponent = useMemo(() => renewingConnection ? null : (<ListEmptyView happy title="No broken connections" subTitle="If you want to renew a connection, you can do it in account overview or bank connections" onCard/>), [renewingConnection]);
    const ListHeaderComponent = useMemo(() => (<ScreenInfoHeader title="Broken connections" subTitle="We have issues connecting with the banks below. Please try renewing connection"/>), []);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const contentContainerStyle = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, paddingHorizontalStyle);
    return (<AppView withFrame={false}>
        <Animated.FlatList onScroll={scrollHandler} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} data={allConnections} renderItem={renderItem} keyExtractor={keyExtractor} ItemSeparatorComponent={Spacer.H16} ListHeaderComponent={ListHeaderComponent} ListEmptyComponent={ListEmptyComponent} contentContainerStyle={contentContainerStyle}/>
      </AppView>);
}, ({ colors }) => ({
    title: 'Broken connections',
    hideTitleOnScroll: true,
    headerTintColor: colors.text.primary,
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
export default BrokenConnectionsScreen;
